/**
 * useCurrencyChange Hook (HDR-5, PREF-001)
 *
 * Handles currency changes with optimistic UI updates and backend sync.
 * Similar pattern to useLanguageSwitcher for consistency.
 * PREF-001: Added localStorage persistence for anonymous users via preferencesStore.
 */

import { useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@teletienda/shared-auth-ui';
import { useQueryClient } from '@tanstack/react-query';
import { coreAPI_Service } from '@/lib/api/core';
import {
  usePreferencesStore,
  DEFAULT_CURRENCY,
  type SupportedCurrency,
} from '@/stores/preferencesStore';

// Re-export for backward compatibility
export { DEFAULT_CURRENCY, type SupportedCurrency };

/**
 * Custom hook for currency switching with backend sync
 */
export function useCurrencyChange() {
  const { t } = useTranslation('common');
  const { user, token, fetchProfile } = useAuth();
  const queryClient = useQueryClient();
  const { currency: anonymousCurrency, setCurrency: setAnonymousCurrency } = usePreferencesStore();

  /**
   * Change currency and sync with backend (logged-in) or localStorage (anonymous)
   */
  const changeCurrency = useCallback(
    async (currency: string) => {
      // Get current currency for comparison
      const currentCurrency = (user as any)?.preferred_currency || anonymousCurrency;

      // Skip if same currency
      if (currency === currentCurrency) {
        return;
      }

      try {
        // Sync with backend if user is authenticated
        if (user && token) {
          try {
            await coreAPI_Service.updateProfile({ preferred_currency: currency });
            console.log(`[Currency] Preference synced to backend: ${currency}`);

            // Refresh user data to update local state
            // This will trigger a re-fetch of the user profile
            await fetchProfile();

            // Invalidate queries that depend on currency
            queryClient.invalidateQueries({ queryKey: ['exchangeRates'] });
          } catch (backendError) {
            // Don't show error to user if backend sync fails
            // The currency change in UI was successful, which is most important
            console.warn('[Currency] Failed to sync currency to backend:', backendError);
          }
        } else {
          // PREF-001: Save to localStorage via preferencesStore for anonymous users
          setAnonymousCurrency(currency as SupportedCurrency);
          console.log(`[Currency] Preference saved to localStorage: ${currency}`);

          // Invalidate queries that depend on currency
          queryClient.invalidateQueries({ queryKey: ['exchangeRates'] });
        }

        notifications.show({
          title: t('currency'),
          message: t('currency_changed'),
          color: 'green',
        });
      } catch (error) {
        console.error('Failed to change currency:', error);
        notifications.show({
          title: t('error'),
          message: t('currency_change_failed'),
          color: 'red',
        });
      }
    },
    [user, token, fetchProfile, queryClient, anonymousCurrency, setAnonymousCurrency, t]
  );

  /**
   * Get current currency: from user profile for logged-in, from store for anonymous
   */
  const currentCurrency: SupportedCurrency =
    ((user as any)?.preferred_currency as SupportedCurrency) || anonymousCurrency;

  return {
    currentCurrency,
    changeCurrency,
  };
}
